// Visual tokens for the parallax Show Carousel variant.
// Values mirror the Figma comp (UF - Carousel refresh): card 267x150 desktop / 213x120 tablet /
// 171x96 mobile, 8px radius, 20px strip gap / 24px row gap, always-on translucent title bar that
// expands to a solid-black two-line bar on hover. Section heading is PF DIN Text Pro Medium, 36px
// desktop / 30px tablet / 28px mobile, centered on mobile and left-aligned from tablet up. The tile
// label's own type (14px medium title / 14px regular network, flat across breakpoints) comes from the
// DDS `body3medium` / `body3` variants, not from these styles — see labelTitle below.

import { colorPalette } from "@directv-digital/dds.ui.dtv-theme";

export const PARALLAX_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

// How far (px) a row translates horizontally across the full scroll of the section.
// Rows alternate sign so adjacent rows move in opposite directions.
export const PARALLAX_TRAVEL = 160;

// Poster card is a 16:9 landscape tile (267x150 in the comp).
export const CARD_ASPECT_RATIO = "16 / 9";

// What BOTH label lines carry, in one declaration. Spread into each rather than shared by
// reference so a line can still add to it — but never so that one line silently keeps a property
// the other loses, which is the drift the label typography work exists to fix.
const labelLine = {
  display: "block",
  color: colorPalette.neutral_white.main,
  textShadow: "0 1px 2px rgba(0,0,0,0.85)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};

export const parallaxStyles = {
  // Outer section clips the horizontal parallax drift at its own edges. The WnC content column it
  // sits in is full-bleed up to the page wrapper's 2000px cap, so 100% is the full bleed the comp
  // wants (strips go edge-to-edge; the title keeps its gutter); past that cap the section stops at
  // the same 2000px boundary every other page section respects. Width stays parent-relative on
  // purpose: a viewport-relative width also counts the classic (non-overlay) vertical scrollbar
  // gutter, which makes the box wider than the document can display and scrolls the page sideways.
  section: (backgroundColor?: string) => ({
    position: "relative" as const,
    overflow: "hidden",
    width: "100%",
    maxWidth: "100%",
    backgroundColor: backgroundColor || "transparent",
    py: {
      xs: "32px",
      sm: "40px",
      md: "56px"
    }
  }),
  // Title: PF DIN Text Pro Medium, per the comp — 28px mobile / 30px tablet / 36px desktop,
  // tracking -2% (letterSpacing -0.02em scales per size). Centered on mobile, left-aligned from
  // tablet up. Breakpoints follow the DDS theme (sm=600, md=1200): sm covers tablet, md covers desktop.
  heading: (color?: string) => ({
    display: "block",
    color: color || colorPalette.black.main,
    textAlign: {
      xs: "center",
      sm: "left"
    },
    fontSize: {
      xs: "28px",
      sm: "30px",
      md: "36px"
    },
    fontWeight: 500,
    lineHeight: 1.2,
    letterSpacing: "-0.02em",
    px: {
      xs: "20px",
      sm: "40px",
      md: "140px"
    },
    mb: {
      xs: "16px",
      md: "24px"
    }
  }),
  subheading: (color?: string) => ({
    display: "block",
    color: color || colorPalette.black.main,
    textAlign: "left" as const,
    px: {
      xs: "20px",
      sm: "40px",
      md: "140px"
    },
    mb: {
      xs: "12px",
      md: "16px"
    }
  }),
  // Wraps the two rows. 24px row gap.
  rowsWrapper: {
    display: "flex",
    flexDirection: "column" as const,
    gap: {
      xs: "16px",
      md: "24px"
    }
  },
  // Wraps each row's card layer at every breakpoint. Strips bleed edge-to-edge (px 0).
  // When the scroll-linked drift is active it moves the rows, so this stays overflow-visible (the
  // outer section does the horizontal clipping) and lets a hovered (scaled-up) card grow vertically.
  // When the drift is OFF (reduced motion / single row) the rows are static, so this becomes a native
  // horizontal scroll container instead, keeping every card reachable by swipe/keyboard rather than
  // clipping the row. `scrollPadding` reveals a focused card fully instead of pinning it at the edge.
  viewport: (driftActive: boolean) => ({
    position: "relative" as const,
    overflowX: driftActive ? "visible" : "auto",
    overflowY: "visible" as const,
    scrollbarWidth: "none" as const,
    WebkitOverflowScrolling: "touch" as const,
    scrollPaddingInline: "20px",
    "&::-webkit-scrollbar": { display: "none" },
    px: 0
  }),
  // Scroll-linked parallax layer. Its translateX is written every rAF frame, so it carries no CSS
  // transition (a per-frame transition would lag). Transform is only written while the drift is
  // active; otherwise it rests at its natural position (the row scrolls natively).
  parallaxLayer: {
    willChange: "transform"
  },
  // Holds the cards (the measured flex container for repeat-to-fill). 20px gap between posters.
  track: {
    display: "flex",
    gap: {
      xs: "16px",
      md: "20px"
    },
    width: "max-content"
  },
  card: (bgColor?: string) => ({
    position: "relative" as const,
    flex: "0 0 auto",
    width: {
      xs: "171px",
      sm: "213px",
      md: "267px"
    },
    aspectRatio: CARD_ASPECT_RATIO,
    borderRadius: "8px",
    overflow: "hidden",
    cursor: "pointer",
    backgroundColor: bgColor || "#e4e4e4",
    boxShadow: "none",
    transition: `transform 0.3s ${PARALLAX_EASING}, box-shadow 0.3s ${PARALLAX_EASING}`,
    "@media (hover: hover)": {
      "&:hover": {
        transform: "scale(1.13)",
        boxShadow: "0px 8px 24px rgba(0,0,0,0.45)",
        zIndex: 2
      },
      "&:hover .parallax-label": {
        backgroundColor: "#000000"
      },
      "&:hover .parallax-label-network": {
        opacity: 1,
        maxHeight: "20px"
      }
    },
    // Dual-ring focus so it stays visible on both light and dark carousel backgrounds.
    "&:focus-visible": {
      outline: `2px solid ${colorPalette.neutral_white.main}`,
      outlineOffset: "2px",
      boxShadow: "0 0 0 4px rgba(0,0,0,0.6)"
    },
    // Mirror the hover reveal for keyboard focus (no pointer required) — solid bar + network line.
    "&:focus-visible .parallax-label": {
      backgroundColor: "#000000"
    },
    "&:focus-visible .parallax-label-network": {
      opacity: 1,
      maxHeight: "20px"
    }
  }),
  cardImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
    display: "block"
  },
  // Always-visible title bar pinned to the bottom of the poster (AC5). Background is opaque enough
  // (0.72 black) to keep the white title >= 4.5:1 over any poster (WCAG 1.4.3); a text-shadow adds a
  // safety halo. Expands to a solid-black two-line bar on hover/focus to reveal the network (AC6).
  label: {
    position: "absolute" as const,
    left: 0,
    right: 0,
    bottom: 0,
    minHeight: "25px",
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    gap: "2px",
    padding: "4px 12px",
    backgroundColor: "rgba(0,0,0,0.72)",
    borderBottomLeftRadius: "8px",
    borderBottomRightRadius: "8px",
    transition: `background-color 0.3s ${PARALLAX_EASING}`
  },
  // Both label lines carry ONLY what their DDS text variant does not: colour, shadow and the
  // single-line ellipsis. Size, weight, family, line height and tracking come from the variants
  // (`body3medium` / `body3`), which are the comp's `Body 3 medium` / `Body 3` styles exactly (AC1).
  // Do not add fontSize here: a flat `sx` size overwrites only the variant's base declaration, while
  // the variant's per-media-query sizes are emitted after it and win at every breakpoint.
  labelTitle: {
    ...labelLine
  },
  // Second-line wrapper: carries only the hover/focus reveal, so the text styling below can live on
  // the DtvText itself.
  labelNetwork: {
    opacity: 0,
    maxHeight: 0,
    overflow: "hidden",
    transition: `opacity 0.3s ${PARALLAX_EASING}, max-height 0.3s ${PARALLAX_EASING}`
  },
  labelNetworkText: {
    ...labelLine
  }
};
